<template>
  <div v-if="isNavigationEnabled" class="navigation">
    <v-subheader v-if="title">{{ title }}</v-subheader>
    <div v-for="(item, i) in items" :key="i">
      <v-list-item
        :data-test-menu="item.text"
        :href="item.target ? item.link : undefined"
        :to="item.target ? undefined : item.link"
        v-if="isUserPermittedToSee(item) && item.isEnabled"
        :target="item.target"
      >
        <v-list-item-icon>
          <v-badge v-if="item.badge" :content="item.badge" :color="color" overlap>
            <v-icon v-text="item.icon"></v-icon>
          </v-badge>
          <v-icon v-text="item.icon" v-else></v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title v-text="$t(item.text)"></v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </div>
  </div>
  <div v-else></div>
</template>

<script lang="ts">
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IMenuListItem } from "@/lib/interfaces/menu-list-item.interface";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { UserModule } from "@/store/modules/me-user.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import { PartnerModule } from "@/store/modules/partner";

/**
 * Layout for navigation lists in navigation bar
 */
@Component({})
export default class TheNavigationLayout extends Vue {
  /**
   * The items to show in the navigation
   */
  @Prop()
  items!: IMenuListItem[];

  /**
   * The title of the navigation block
   */
  @Prop()
  title!: string;

  /**
   * Is the navigation hidden
   */
  @Prop({ default: true })
  isEnabled?: boolean;

  /**
   * checks if user is permitted to see the item
   *
   * @param item the item to check
   */
  isUserPermittedToSee(item: IMenuListItem) {
    if (!item.permission) {
      return true;
    }

    const { resource, action, field } = item.permission;

    return UserModule.abilities.can(action, resource, field);
  }

  /**
   * Checks if the navigation is enabled or should be hidden
   *
   * the navigation is disabled if the isEnabled flag is set to false
   * the navigation is disabled if every item is hidden by permission
   * the navigation is enabled if it is not disabled
   */
  get isNavigationEnabled() {
    if (!this.isEnabled) {
      return false;
    }
    let isNavigationItemEnabled = false;
    for (const item of this.items) {
      if (item.permission === undefined) {
        // if there is an item that has no permission requirement, the item is visible
        isNavigationItemEnabled = true;
      } else if (this.isUserPermittedToSee(item)) {
        // the user is allowed to see the item
        isNavigationItemEnabled = true;
      }
    }

    return isNavigationItemEnabled;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get color() {
    return this.partner.settings?.isVuetifyInPartnerColors ? getDefaultPartnerColor() : ConfigModule.color.primary;
  }

  /**
   * Set color of active navigation element in partner color
   */
  setSelectedElementColor() {
    const partnerColor = getDefaultPartnerColor();
    const navigationElements = document.getElementsByClassName("navigation");

    for (const navigationElement of navigationElements || []) {
      for (const activeNavigationElement of navigationElement.getElementsByClassName("v-list-item--active") || []) {
        (activeNavigationElement as HTMLElement).style["color"] = partnerColor;
      }
    }
  }

  mounted() {
    if (
      (ConfigModule.appContext === AppContextEnum.THG || ConfigModule.appContext === AppContextEnum.REPORT) &&
      FeatureModule.isNavigationInPartnerColors
    ) {
      this.setSelectedElementColor();
    }
  }
}
</script>

<style scoped>
.v-list-item--dense .v-list-item__title {
  font-weight: 400 !important;
}
</style>
